import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PageHeader, AlertMessage, FormNavigation, VehicleEditPageSkeleton } from '../../components/ui'
import { Stepper } from '../../components/vehicles'
import { VehicleEditStep1, VehicleEditStep2, VehicleEditStep3, VehicleEditStep4, VehicleEditStep5, VehicleEditStep6 } from '../../components/vehicles/steps'
import { VehicleDocument } from '../../components/form'
import { ExistingVehicleDocument } from '../../components/vehicles'
import { getStepValidator } from '../../utils/vehicleValidation'
import { useVehicleOptions } from '../../hooks/useVehicleOptions'
import { buildFeaturesByCategoryPayload, getFlatFeaturesFromVehicleResponse } from './utils/vehicleFeaturesApi'
import { apiService } from '../../services/api'

interface VehicleFormData {
 branchId?: string
 name: string
 brandId: string
 brandName: string
 modelId: string
 modelName: string
 manufactureYear: string
 modelYear: string
 price: string
 minimumPrice: string
 purchasePrice: string
 hidePricing: boolean
 mileage: string
 fuelType: string
 transmission: string
 color: string
 category: string
 type: string
 condition: string
 status: string
 doors: string
 steering: string
 armorLevel?: string
 specialCategory?: string
 characteristics?: string[]
 features: {
 comfort?: Record<string, boolean>
 safety?: Record<string, boolean>
 technology?: Record<string, boolean>
 convenience?: Record<string, boolean>
 performance?: Record<string, boolean>
 lighting?: Record<string, boolean>
 adas?: Record<string, boolean>
 }
 plate?: string
 chassis?: string
 renavam?: string
 isAuction: boolean
 auctionCategory?: string
 auctionSubtype?: string
 auctionDamageLevel?: string
 isInspected: boolean
 inspectionDate?: string
 description: string
 photos: File[]
 documents: VehicleDocument[]
 videos: File[]
 videoUrl?: string
 instagramUrl?: string
 youtubeUrl?: string
 tiktokUrl?: string
 facebookUrl?: string
 motorcycleEngineType?: string
 motorcycleCoolingType?: string
 motorcycleStarterType?: string
 motorcycleBrakeType?: string
 motorcycleFrontBrakeType?: string
 motorcycleRearBrakeType?: string
 motorcycleFeedingType?: string
 motorcycleStyle?: string
 motorcycleCategory?: string
 motorcycleCylinderCapacity?: string
 motorcycleGears?: string
 motorcycleSecondaryColor?: string
 // Caminhões
 truckBodyType?: string
 truckCabType?: string
 truckAxles?: string
 truckTraction?: string
 truckGrossVehicleWeight?: string
 truckPayload?: string
 truckTires?: string
 // Ônibus
 busBodyBrand?: string
 busBodyModel?: string
 busOperationType?: string
 busFleetNumber?: string
 busSeatedCapacity?: string
 busStandingCapacity?: string
 busWheelchairCapacity?: string
}


export const VehicleEditPage: React.FC = () => {
 const navigate = useNavigate()
 const { id } = useParams<{ id: string }>()
 const [isSubmitting, setIsSubmitting] = useState(false)
 const [isLoadingVehicle, setIsLoadingVehicle] = useState(true)
 const [errors, setErrors] = useState<Record<string, string>>({})
 const [successMessage, setSuccessMessage] = useState('')
 const [initialVehicleData, setInitialVehicleData] = useState<any>(null)
 const [currentStep, setCurrentStep] = useState(1)
 const totalSteps = 6

 const { 
 options: vehicleOptions,
 isLoading: optionsLoading,
 error: optionsError
 } = useVehicleOptions()

 const [formData, setFormData] = useState<VehicleFormData>({
 branchId: '',
 name: '',
 brandId: '',
 brandName: '',
 modelId: '',
 modelName: '',
 manufactureYear: '',
 modelYear: '',
 price: '',
 minimumPrice: '',
 purchasePrice: '',
 hidePricing: false,
 mileage: '',
 fuelType: '',
 transmission: '',
 color: '',
 category: '',
 type: '',
 condition: '',
 status: '',
 doors: '',
 steering: '',
 armorLevel: '',
 specialCategory: '',
 characteristics: [],
 features: {},
 plate: '',
 chassis: '',
 renavam: '',
 isAuction: false,
 auctionCategory: '',
 auctionSubtype: '',
 auctionDamageLevel: '',
 isInspected: false,
 inspectionDate: '',
 description: '',
 photos: [],
 documents: [],
 videos: [],
 videoUrl: '',
 instagramUrl: '',
 youtubeUrl: '',
 tiktokUrl: '',
 facebookUrl: '',
 motorcycleEngineType: '',
 motorcycleCoolingType: '',
 motorcycleStarterType: '',
 motorcycleBrakeType: '',
 motorcycleFrontBrakeType: '',
 motorcycleRearBrakeType: '',
 motorcycleFeedingType: '',
 motorcycleStyle: '',
 motorcycleCategory: '',
 motorcycleCylinderCapacity: '',
 motorcycleGears: '',
 motorcycleSecondaryColor: '',
 truckBodyType: '',
 truckCabType: '',
 truckAxles: '',
 truckTraction: '',
 truckGrossVehicleWeight: '',
 truckPayload: '',
 truckTires: '',
 busBodyBrand: '',
 busBodyModel: '',
 busOperationType: '',
 busFleetNumber: '',
 busSeatedCapacity: '',
 busStandingCapacity: '',
 busWheelchairCapacity: '',
 })

 const [primaryPhotoIndex, setPrimaryPhotoIndex] = useState<number>(0)
 const [existingPhotos, setExistingPhotos] = useState<Array<{ id: string; url: string; isPrimary?: boolean; order?: number }>>([])
 const [photosToRemove, setPhotosToRemove] = useState<string[]>([])
 const [existingDocuments, setExistingDocuments] = useState<ExistingVehicleDocument[]>([])
 const [documentsToRemove, setDocumentsToRemove] = useState<string[]>([]) // IDs das fotos existentes a serem removidas
 
 const errorsSectionRef = useRef<HTMLDivElement>(null)
 const formContainerRef = useRef<HTMLDivElement>(null)

 const handleRemoveExistingPhoto = (photoId: string) => {
 setPhotosToRemove(prev => [...prev, photoId])
 setExistingPhotos(prev => prev.filter(photo => photo.id !== photoId))
 }

 const handleRemoveExistingDocument = (docId: string) => {
 setDocumentsToRemove(prev => [...prev, docId])
 setExistingDocuments(prev => prev.filter(doc => doc.id !== docId))
 }

 const handleSetPrimaryPhoto = (photoId: string) => {
 setExistingPhotos(prev => prev.map(photo => ({
 ...photo,
 isPrimary: photo.id === photoId
 })))
 }

 // Função para fazer scroll suave ao topo
 const scrollToTop = () => {
 // Usa requestAnimationFrame para garantir que a renderização do novo step aconteceu
 requestAnimationFrame(() => {
 if (formContainerRef.current) {
 formContainerRef.current.scrollIntoView({ 
 behavior: 'smooth', 
 block: 'start' 
 })
 } else {
 window.scrollTo({ 
 top: 0, 
 behavior: 'smooth' 
 })
 }
 })
 }

 useEffect(() => {
 const loadVehicleData = async () => {
 if (!id) {
 setIsLoadingVehicle(false)
 return
 }

 setIsLoadingVehicle(true)
 try {
 const vehicle = await apiService.getVehicleById(id) as any

 console.log('📦 Veículo carregado:', vehicle)

 const { basic, catalog, identification, specifications, commercial, auction, pricing, characteristics, media } = vehicle
 
 console.log('🎯 Dados de leilão recebidos:', auction)
 
 const socialMedia = media?.socialMedia || []
 const instagramUrl = socialMedia.find((s: any) => s.type === 'instagram')?.url || ''
 const youtubeUrl = socialMedia.find((s: any) => s.type === 'youtube')?.url || ''
 const tiktokUrl = socialMedia.find((s: any) => s.type === 'tiktok')?.url || ''
 const facebookUrl = socialMedia.find((s: any) => s.type === 'facebook')?.url || ''
 
 // Converter features da API para formato categorizado
 const categorizedFeatures = convertFeaturesFromAPI(getFlatFeaturesFromVehicleResponse(vehicle))

 setFormData({
 branchId: basic?.branchId || (vehicle as any)?.branch?.id || '',
 name: basic?.name || '',
 brandId: catalog?.brand?.id || '',
 brandName: catalog?.brand?.name || '',
 modelId: catalog?.model?.id || '',
 modelName: catalog?.model?.name || '',
 manufactureYear: catalog?.manufactureYear?.toString() || '',
 modelYear: catalog?.modelYear?.toString() || '',
 price: formatPriceFromNumber(pricing?.sale?.price || 0),
 minimumPrice: formatPriceFromNumber(pricing?.sale?.minimumPrice || 0),
 purchasePrice: formatPriceFromNumber(pricing?.purchase?.price || 0),
 hidePricing: pricing?.sale?.hidePrice || false,
 mileage: formatMileage(specifications?.mileage?.toString() || '0'),
 fuelType: specifications?.engine?.type || '',
 transmission: specifications?.engine?.transmission || '',
 color: specifications?.body?.color || '',
 category: basic?.category || '',
 type: basic?.type || '',
 condition: basic?.condition || '',
 status: basic?.status || '',
 doors: specifications?.body?.doors !== undefined && specifications?.body?.doors !== null
 ? specifications.body.doors.toString() 
 : '',
 steering: commercial?.steeringType || '',
 armorLevel: commercial?.armorLevel || '',
 specialCategory: commercial?.specialCategory || '',
 characteristics: characteristics || [],
 features: categorizedFeatures,
 plate: identification?.plate || '',
 chassis: identification?.chassis || '',
 renavam: identification?.renavam || '',
 isAuction: auction?.isAuction || false,
 auctionCategory: auction?.category || '',
 auctionSubtype: auction?.subtype || '',
 auctionDamageLevel: auction?.damageLevel || '',
 isInspected: commercial?.inspected || false,
 inspectionDate: commercial?.inspectionDate ? commercial.inspectionDate.split('T')[0] : '',
 description: basic?.description || '',
 photos: [],
 documents: [],
 videos: [],
 videoUrl: commercial?.videoUrl || '',
 instagramUrl: instagramUrl,
 youtubeUrl: youtubeUrl,
 tiktokUrl: tiktokUrl,
 facebookUrl: facebookUrl,
 motorcycleEngineType: specifications?.motorcycle?.engineType || '',
 motorcycleCoolingType: specifications?.motorcycle?.coolingType || '',
 motorcycleStarterType: specifications?.motorcycle?.starterType || '',
 motorcycleBrakeType: specifications?.motorcycle?.frontBrake || '',
 motorcycleFrontBrakeType: specifications?.motorcycle?.frontBrake || '',
 motorcycleRearBrakeType: specifications?.motorcycle?.rearBrake || '',
 motorcycleFeedingType: specifications?.motorcycle?.feedingType || '',
 motorcycleStyle: specifications?.motorcycle?.style || '',
 motorcycleCategory: specifications?.motorcycle?.category || '',
 motorcycleCylinderCapacity: specifications?.motorcycle?.cylinderCapacity?.toString() || '',
 motorcycleGears: specifications?.motorcycle?.gears?.toString() || '',
 motorcycleSecondaryColor: specifications?.body?.secondaryColor || '',
 truckBodyType: specifications?.truck?.bodyType || '',
 truckCabType: specifications?.truck?.cabType || '',
 truckAxles: specifications?.truck?.axles?.toString() || '',
 truckTraction: specifications?.truck?.traction || '',
 truckGrossVehicleWeight: specifications?.truck?.grossVehicleWeight?.toString() || '',
 truckPayload: specifications?.truck?.payload?.toString() || '',
 truckTires: specifications?.truck?.tires?.toString() || '',
busBodyBrand: specifications?.bus?.bodyBrand || '',
busBodyModel: specifications?.bus?.bodyModel || '',
busOperationType: specifications?.bus?.operationType || '',
busFleetNumber: specifications?.bus?.fleetNumber || '',
busSeatedCapacity: specifications?.bus?.seatedCapacity?.toString() || '',
busStandingCapacity: specifications?.bus?.standingCapacity?.toString() || '',
busWheelchairCapacity: specifications?.bus?.wheelchairCapacity?.toString() || '',
 })

 console.log('✅ FormData preenchido')
 console.log('📋 isInspected:', commercial?.inspected)
 console.log('📋 inspectionDate (original):', commercial?.inspectionDate)
 console.log('📋 inspectionDate (formatada):', commercial?.inspectionDate ? commercial.inspectionDate.split('T')[0] : '')
 console.log('🚪 Doors carregado da API:', specifications?.body?.doors)
 console.log('🚪 Doors no formData:', specifications?.body?.doors?.toString())

 setInitialVehicleData({
 category: basic?.category,
 brandId: catalog?.brand?.id,
 brandName: catalog?.brand?.name,
 modelId: catalog?.model?.id,
 modelName: catalog?.model?.name
 })

 if (media?.photos && media.photos.length > 0) {
 const sortedPhotos = [...media.photos].sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
 setExistingPhotos(sortedPhotos.map((photo: any) => ({
 id: photo.id,
 url: photo.url,
 isPrimary: photo.isPrimary,
 order: photo.order
 })))
 console.log('📸 Fotos carregadas:', sortedPhotos.length)
 }

 if (media?.documents && media.documents.length > 0) {
 setExistingDocuments(media.documents.map((doc: any) => ({
 id: doc.id,
 type: doc.type,
 name: doc.name,
 url: doc.url,
 expiryDate: doc.expiryDate,
 uploadedBy: doc.uploadedBy,
 createdAt: doc.createdAt
 })))
 console.log('📄 Documentos carregados:', media.documents.length)
 }

 } catch (error) {
 console.error('Erro ao carregar veículo:', error)
 alert('Erro ao carregar dados do veículo')
 navigate('/vehicles')
 } finally {
 setIsLoadingVehicle(false)
 }
 }

 loadVehicleData()
 }, [id])

 const validateForm = () => {
 const newErrors: Record<string, string> = {}

 if (!formData.name.trim()) {
 newErrors.name = 'Nome é obrigatório'
 }

 if (!formData.brandId) {
 newErrors.brand = 'Marca é obrigatória'
 }

 if (!formData.modelId) {
 newErrors.model = 'Modelo é obrigatório'
 }

 if (!formData.description.trim()) {
  newErrors.description = 'Descrição é obrigatória'
 } else if (formData.description.length < 10) {
  newErrors.description = 'Descrição deve ter pelo menos 10 caracteres'
 }

 setErrors(newErrors)
 
 if (Object.keys(newErrors).length > 0) {
 console.log('❌ Erros de validação:', newErrors)
 setTimeout(() => {
 errorsSectionRef.current?.scrollIntoView({ 
 behavior: 'smooth', 
 block: 'start' 
 })
 }, 100)
 } else {
 console.log('✅ Nenhum erro de validação')
 }
 
 return Object.keys(newErrors).length === 0
 }

 const handleInputChange = (field: string, value: any) => {
 setFormData(prev => ({ ...prev, [field]: value }))
 if (errors[field]) {
 setErrors(prev => ({ ...prev, [field]: '' }))
 }
 }

 // Formata valores que já vêm em reais da API (não divide por 100)
 const formatPriceFromNumber = (value: number | string) => {
 const numericValue = typeof value === 'string' ? parseFloat(value) : value
 if (isNaN(numericValue) || numericValue === 0) return ''
 
 return new Intl.NumberFormat('pt-BR', {
 style: 'currency',
 currency: 'BRL',
 minimumFractionDigits: 2,
 maximumFractionDigits: 2
 }).format(numericValue)
 }

 const formatMileage = (value: string) => {
 const numbers = value.replace(/\D/g, '')
 return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
 }

 // Converter features planas da API para formato categorizado
 const convertFeaturesFromAPI = (apiFeatures: any) => {
 if (!apiFeatures) return {}

 const categorizedFeatures: any = {
 comfort: {},
 safety: {},
 technology: {},
 convenience: {},
 performance: {},
 lighting: {},
 adas: {}
 }

 // Mapear campos da API para categorias
 const featureMap: Record<string, string> = {
 // Conforto
 airConditioning: 'comfort',
 climateControl: 'comfort',
 heatedSeats: 'comfort',
 cooledSeats: 'comfort',
 leatherSeats: 'comfort',
 electricSeats: 'comfort',
 memorySeats: 'comfort',
 sunroof: 'comfort',
 panoramicRoof: 'comfort',
 
 // Tecnologia
 multimediaCenter: 'technology',
 touchScreen: 'technology',
 gps: 'technology',
 bluetooth: 'technology',
 androidAuto: 'technology',
 appleCarPlay: 'technology',
 wirelessCharger: 'technology',
 
 // Segurança
 abs: 'safety',
 ebd: 'safety',
 esc: 'safety',
 tractionControl: 'safety',
 hillAssist: 'safety',
 frontAirbags: 'safety',
 sideAirbags: 'safety',
 curtainAirbags: 'safety',
 rearCamera: 'safety',
 camera360: 'safety',
 frontSensors: 'safety',
 rearSensors: 'safety',
 blindSpotMonitor: 'safety',
 tirePressure: 'safety',
 
 // ADAS
 adaptiveCruise: 'adas',
 laneKeepAssist: 'adas',
 laneDepartureWarning: 'adas',
 automaticBraking: 'adas',
 collisionWarning: 'adas',
 parkAssist: 'adas',
 trafficSignRecognition: 'adas',
 driverAttentionMonitor: 'adas',
 
 // Iluminação
 ledHeadlights: 'lighting',
 xenonHeadlights: 'lighting',
 automaticHeadlights: 'lighting',
 daytimeRunningLights: 'lighting',
 fogLights: 'lighting',
 adaptiveLighting: 'lighting',
 
 // Conveniência
 electricWindows: 'convenience',
 electricMirrors: 'convenience',
 foldingMirrors: 'convenience',
 heatedMirrors: 'convenience',
 rainSensor: 'convenience',
 lightSensor: 'convenience',
 alloyWheels: 'convenience',
 keylessEntry: 'convenience',
 pushButtonStart: 'convenience',
 remoteStart: 'convenience',
 electricTrunk: 'convenience',
 handsFreetrunk: 'convenience',
 
 // Performance
 cruiseControl: 'performance',
 paddleShifters: 'performance',
 sportMode: 'performance',
 ecoMode: 'performance',
 adjustableSuspension: 'performance',
 airSuspension: 'performance',
 limitedSlipDiff: 'performance',
 fourWheelDrive: 'performance',
 allWheelDrive: 'performance'
 }

 // Converter
 Object.entries(apiFeatures).forEach(([key, value]) => {
 if (featureMap[key] && value === true) {
 categorizedFeatures[featureMap[key]][key] = true
 }
 })

 console.log('🔄 Features convertidas (API → Form):', categorizedFeatures)
 return categorizedFeatures
 }

 const nextStep = () => {
 console.log('🔄 NextStep chamado - Step atual:', currentStep)
 
 if (currentStep < totalSteps) {
 console.log('🔍 Iniciando validação do Step', currentStep)
 console.log('📋 FormData atual:', formData)
 
 const validator = getStepValidator(currentStep)
 console.log('🔧 Validator obtido:', validator.name || 'função anônima')
 
 const validation = validator(formData)
 console.log('📊 Resultado da validação:', validation)
 
 if (!validation.isValid) {
 setErrors(validation.errors)
 scrollToTop()
 console.log('❌ BLOQUEADO - Validação falhou no Step', currentStep)
 console.log('❌ Erros:', validation.errors)
 return
 }
 
 console.log('✅ Validação passou no Step', currentStep)
 setErrors({})
 setCurrentStep(currentStep + 1)
 scrollToTop()
 }
 }

 const prevStep = () => {
 if (currentStep > 1) {
 setCurrentStep(currentStep - 1)
 scrollToTop()
 }
 }

 const goToStep = (step: number) => {
 if (step >= 1 && step <= totalSteps) {
 setCurrentStep(step)
 scrollToTop()
 }
 }

 const uploadPhotos = async (): Promise<string[]> => {
 if (formData.photos.length === 0) {
 return []
 }

 const reorderedPhotos = [...formData.photos]
 if (primaryPhotoIndex !== 0) {
 const primaryPhoto = reorderedPhotos[primaryPhotoIndex]
 reorderedPhotos.splice(primaryPhotoIndex, 1)
 reorderedPhotos.unshift(primaryPhoto)
 }

 const formDataPhotos = new FormData()
 reorderedPhotos.forEach((photo) => {
 formDataPhotos.append('photos', photo)
 })

 const response = await apiService.uploadTempVehiclePhotos(formDataPhotos)
 
 return response.photos.map((photo: { filename: string; url: string }) => photo.filename)
 }

 const uploadDocuments = async (): Promise<Array<{ filename: string; category: string }>> => {
 if (formData.documents.length === 0) {
 return []
 }

 const formDataDocuments = new FormData()
 formData.documents.forEach((doc, index) => {
 formDataDocuments.append('docs', doc.file)
 formDataDocuments.append(`categories[${index}]`, doc.category)
 })

 const response = await apiService.uploadTempVehicleDocuments(formDataDocuments)
 return response.documents.map((doc: { filename: string; url: string; category?: string }, index: number) => ({
 filename: doc.filename,
 category: doc.category || formData.documents[index]?.category || 'other'
 }))
 }

 const handleSubmit = async () => {
 console.log('🚀 Salvar Alterações clicado')
 
 if (isSubmitting) {
 console.log('⚠️ Já está salvando, aguarde...')
 return
 }
 
 console.log('🔄 Validando formulário...')
 if (!validateForm()) {
 console.log('❌ Validação falhou')
 return
 }

 console.log('✅ Validação passou - Iniciando salvamento...')
 setIsSubmitting(true)
 try {
 // Upload de documentos PRIMEIRO
 let tempDocuments: Array<{ filename: string; category: string }> = []
 if (formData.documents.length > 0) {
 console.log('📄 Fazendo upload de', formData.documents.length, 'documentos...')
 tempDocuments = await uploadDocuments()
 console.log('✅ Upload de documentos concluído:', tempDocuments)
 } else {
 console.log('ℹ️ Nenhum documento para upload')
 }

 // Upload de fotos DEPOIS
 let tempPhotos: string[] = []
 if (formData.photos.length > 0) {
 console.log('📸 Fazendo upload de', formData.photos.length, 'fotos...')
 tempPhotos = await uploadPhotos()
 console.log('✅ Upload de fotos concluído:', tempPhotos)
 } else {
 console.log('ℹ️ Nenhuma foto para upload')
 }

 console.log('💰 Preço formatado:', formData.price)
 const priceClean = formData.price.replace(/\./g, '').replace(',', '.').replace('R$', '')
 console.log('💰 Preço limpo:', priceClean)
 const priceNumber = parseFloat(priceClean)
 console.log('💰 Preço número:', priceNumber)
 
 if (isNaN(priceNumber) || priceNumber <= 0) {
 console.error('❌ Preço inválido (NaN ou <= 0)')
 throw new Error('Preço inválido. Verifique o valor e tente novamente.')
 }

 // Calcular plateEnd (último dígito da placa)
 const plateEnd = formData.plate && formData.plate.length > 0 
 ? formData.plate.slice(-1) 
 : undefined

 // Montar o payload seguindo a estrutura da API
 const vehicleData: any = {
 basic: {
 branchId: formData.branchId || undefined,
 name: formData.name,
 category: formData.category,
 condition: formData.condition,
 status: formData.status,
 type: formData.type || undefined,
 description: formData.description || undefined
 },
 catalog: {
 brandId: formData.brandId,
 modelId: formData.modelId,
 modelYear: parseInt(formData.modelYear),
 manufactureYear: parseInt(formData.manufactureYear)
 },
 identification: {
 plate: formData.plate || undefined,
 plateEnd: plateEnd,
 chassis: formData.chassis || undefined,
 renavam: formData.renavam || undefined
 },
 specifications: {
 engine: {
 type: formData.fuelType || undefined,
 transmission: formData.transmission || undefined,
 engine: undefined, // Será preenchido pela API
 powerHp: undefined,
 displacement: undefined,
 cylinders: undefined,
 turbo: undefined
 },
 consumption: {
 city: undefined,
 highway: undefined,
 tankCapacity: undefined
 },
 body: {
 doors: formData.doors ? parseInt(formData.doors) : undefined,
 seats: undefined,
 color: formData.color || undefined,
 colorName: undefined,
 secondaryColor: formData.motorcycleSecondaryColor || undefined,
 secondaryColorName: undefined
 },
 mileage: formData.mileage ? parseInt(formData.mileage.replace(/\./g, '')) : 0
 },
 commercial: {
 inspected: formData.isInspected || false,
 inspectionDate: formData.isInspected && formData.inspectionDate 
 ? new Date(formData.inspectionDate).toISOString() 
 : undefined,
 videoUrl: formData.videoUrl || undefined,
 acceptsTrade: false, // Não temos esse campo no form ainda
 armorLevel: formData.armorLevel || undefined,
 specialCategory: formData.specialCategory || undefined,
 steeringType: formData.steering || undefined,
 warranty: {
 months: undefined,
 endDate: undefined
 }
 },
 auction: {
 isAuction: formData.isAuction || false,
 category: formData.auctionCategory || undefined,
 subtype: formData.auctionSubtype || undefined,
 date: undefined,
 house: undefined,
 lot: undefined,
 damageLevel: formData.auctionDamageLevel || undefined,
 notes: undefined
 },
 pricing: {
 sale: {
 price: priceNumber,
 minimumPrice: formData.minimumPrice 
 ? parseInt(formData.minimumPrice.replace(/\D/g, '')) / 100
 : undefined,
 hidePrice: formData.hidePricing || false
 },
 purchase: formData.purchasePrice
 ? {
 price: parseInt(formData.purchasePrice.replace(/\D/g, '')) / 100,
 date: undefined
 }
 : undefined
 },
 featuresByCategory: buildFeaturesByCategoryPayload(formData.category, formData.features),
 characteristics: formData.characteristics || []
 }

 // Se for motocicleta, adicionar dados específicos
 if (formData.category === 'A') {
 vehicleData.specifications.motorcycle = {
 cylinderCapacity: formData.motorcycleCylinderCapacity
 ? parseInt(formData.motorcycleCylinderCapacity)
 : undefined,
 coolingType: formData.motorcycleCoolingType || undefined,
 starterType: formData.motorcycleStarterType || undefined,
 engineType: formData.motorcycleEngineType || undefined,
 gears: formData.motorcycleGears ? parseInt(formData.motorcycleGears) : undefined,
 frontBrake: formData.motorcycleFrontBrakeType || undefined,
 rearBrake: formData.motorcycleRearBrakeType || undefined,
 feedingType: formData.motorcycleFeedingType || undefined,
 style: formData.motorcycleStyle || undefined,
 category: formData.motorcycleCategory || undefined
 }
 }

 // Se for caminhão, adicionar dados específicos
 if (formData.category === 'C') {
 vehicleData.specifications.truck = {
 bodyType: formData.truckBodyType || undefined,
 cabType: formData.truckCabType || undefined,
 traction: formData.truckTraction || undefined,
 axles: formData.truckAxles ? parseInt(formData.truckAxles) : undefined,
 grossVehicleWeight: formData.truckGrossVehicleWeight
 ? parseInt(formData.truckGrossVehicleWeight)
 : undefined,
 payload: formData.truckPayload ? parseInt(formData.truckPayload) : undefined,
 tires: formData.truckTires ? parseInt(formData.truckTires) : undefined
 }
 }

if (formData.category === 'D') {
vehicleData.specifications.bus = {
bodyBrand: formData.busBodyBrand || undefined,
bodyModel: formData.busBodyModel || undefined,
operationType: formData.busOperationType || undefined,
fleetNumber: formData.busFleetNumber || undefined,
seatedCapacity: formData.busSeatedCapacity ? parseInt(formData.busSeatedCapacity) : undefined,
standingCapacity: formData.busStandingCapacity ? parseInt(formData.busStandingCapacity) : undefined,
wheelchairCapacity: formData.busWheelchairCapacity ? parseInt(formData.busWheelchairCapacity) : undefined,
}
}

 // Adicionar fotos
 if (tempPhotos.length > 0) {
 vehicleData.tempPhotos = tempPhotos
 }

 if (tempDocuments.length > 0) {
 vehicleData.tempDocuments = tempDocuments
 }

 // Adicionar fotos a remover
 if (photosToRemove.length > 0) {
 vehicleData.photosToRemove = photosToRemove
 }

 // Adicionar documentos a remover
 if (documentsToRemove.length > 0) {
 vehicleData.documentsToRemove = documentsToRemove
 }

 console.log('🚗 Dados do veículo preparados (estrutura completa):', JSON.stringify(vehicleData, null, 2))

 const response = await apiService.updateVehicle(id!, vehicleData)
 console.log('✅ Veículo atualizado:', response)
 setSuccessMessage('Veículo atualizado com sucesso!')
 
 setTimeout(() => {
 navigate(`/vehicles/${id}`)
 }, 1500)
 
 } catch (error) {
 console.error('❌ Erro ao atualizar veículo:', error)
 if (error instanceof Error) {
 console.error('❌ Mensagem de erro:', error.message)
 console.error('❌ Stack:', error.stack)
 }
 setErrors({ 
 submit: error instanceof Error ? error.message : 'Erro ao atualizar veículo. Tente novamente.' 
 })
 
 setTimeout(() => {
 errorsSectionRef.current?.scrollIntoView({ 
 behavior: 'smooth', 
 block: 'start' 
 })
 }, 100)
 } finally {
 console.log('🏁 Finalizando submit')
 setIsSubmitting(false)
 }
 }

 if (isLoadingVehicle || optionsLoading) {
 return <VehicleEditPageSkeleton />
 }

 return (
 <div className="max-w-5xl mx-auto space-y-6" ref={formContainerRef}>
 <PageHeader
 title="Editar Veículo"
 subtitle={`Edite as informações do veículo em ${totalSteps} etapas`}
 onBack={() => navigate('/vehicles')}
 />

 <Stepper
 currentStep={currentStep}
 totalSteps={totalSteps}
 steps={[
 'Informações Básicas',
 'Preços e Status',
 'Especificações',
 'Documentação',
 'Fotos e Mídias',
 'Resumo Final'
 ]}
 onStepClick={goToStep}
 />

 {successMessage && (
 <AlertMessage variant="success" message={successMessage} />
 )}

 {optionsError && (
 <AlertMessage variant="error" message={optionsError} />
 )}

 {errors.submit && (
 <AlertMessage 
 variant="error" 
 message={errors.submit} 
 innerRef={errorsSectionRef}
 />
 )}
 
 {Object.keys(errors).length > 0 && !errors.submit && (
 <AlertMessage 
 variant="error" 
 title="Corrija os seguintes erros:" 
 errors={errors}
 innerRef={errorsSectionRef}
 />
 )}

 <div className="space-y-6">
 {/* Step 1: Informações Básicas (marca/modelo bloqueados) */}
 {currentStep === 1 && (
 <VehicleEditStep1
 formData={formData}
 errors={errors}
 vehicleOptions={vehicleOptions}
 onInputChange={handleInputChange}

 />
 )}

 {/* Step 2: Preços e Status */}
 {currentStep === 2 && (
 <VehicleEditStep2
 formData={formData}
 errors={errors}
 vehicleOptions={vehicleOptions}
 onInputChange={handleInputChange}
 />
 )}

 {/* Step 3: Especificações + Características */}
 {currentStep === 3 && (
 <VehicleEditStep3
 formData={formData}
 errors={errors}
 vehicleOptions={vehicleOptions}
 onInputChange={handleInputChange}

 />
 )}

 {/* Step 4: Documentação */}
 {currentStep === 4 && (
 <VehicleEditStep4
 formData={formData}
 existingDocuments={existingDocuments}
 onRemoveExistingDocument={handleRemoveExistingDocument}
 errors={errors}
 vehicleOptions={vehicleOptions}
 onInputChange={handleInputChange}

 />
 )}

 {/* Step 5: Fotos e Mídias */}
 {currentStep === 5 && (
 <VehicleEditStep5
 formData={formData}
 existingPhotos={existingPhotos}
 primaryPhotoIndex={primaryPhotoIndex}
 errors={errors}
 onInputChange={handleInputChange}
 onPrimaryPhotoChange={setPrimaryPhotoIndex}
 onRemoveExistingPhoto={handleRemoveExistingPhoto}
 onSetPrimaryPhoto={handleSetPrimaryPhoto}
 />
 )}

 {/* Step 6: Resumo Final */}
 {currentStep === 6 && (
 <VehicleEditStep6
 formData={formData}
 vehicleOptions={vehicleOptions}
 />
 )}

 <FormNavigation
 currentStep={currentStep}
 totalSteps={totalSteps}
 isSubmitting={isSubmitting}
 isDisabled={!formData.category}
 onPrevious={prevStep}
 onNext={nextStep}
 onSubmit={handleSubmit}
 submitLabel="Salvar Alterações"
 />
 </div>
 </div>
 )
}
